import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { NavigationSection, User } from '../../types';
import IntegrationManagement from '../IntegrationManagement';
import UserManagement from '../UserManagement';
import SupplierManagement from '../SupplierManagement';
import DiagnosticsManagement from '../DiagnosticsManagement';
import CategoryManagement from '../CategoryManagement';
import ProductManagement from '../ProductManagement';
import AttributeManagement from '../AttributeManagement';
import B2BClientManagement from '../B2BClientManagement';
import ShopCompanyManagement from '../ShopCompanyManagement';
import ShopOrderManagement from '../ShopOrderManagement';
import MaintenanceManagement from '../MaintenanceManagement';
import OperationsManagement from '../OperationsManagement';
import SettingsManagement from '../SettingsManagement';
import OrderLifetimeManagement from '../SettingsManagement/OrderLifetimeManagement';
import { checkSectionAccess, getAccessDeniedMessage } from '../../utils/accessControl';

interface SectionRouterProps {
    currentSection: NavigationSection;
    currentUser: User | null;
}

const IntegrationsSection: React.FC<{ currentUser: User | null }> = ({ currentUser }) => {
    return <IntegrationManagement currentUser={currentUser} />;
};

const AccessDeniedAlert: React.FC<{ section: NavigationSection }> = ({ section }) => {
    const navigate = useNavigate();
    const { title, message } = getAccessDeniedMessage(section);

    return (
        <div className="container mt-4">
            <div className="alert alert-warning">
                <h5 className="alert-heading">{title}</h5>
                <p>{message}</p>
                <button
                    className="btn btn-primary"
                    onClick={() => navigate('/integrations')}
                >
                    Вернуться к интеграциям
                </button>
            </div>
        </div>
    );
};

export const SectionRouter: React.FC<SectionRouterProps> = ({ currentSection, currentUser }) => {
    // Check access for the current section
    if (!checkSectionAccess(currentSection, currentUser)) {
        return <AccessDeniedAlert section={currentSection} />;
    }

    switch (currentSection) {
        case 'users':
            return <UserManagement currentUser={currentUser} />;
        case 'suppliers':
            return <SupplierManagement currentUser={currentUser} />;
        case 'categories':
            return <CategoryManagement currentUser={currentUser} />;
        case 'diagnostics':
            return <DiagnosticsManagement />;
        case 'products':
            return <ProductManagement currentUser={currentUser} />;
        case 'attributes':
            return <AttributeManagement />;
        case 'b2b-clients':
            return <B2BClientManagement />;
        case 'shop-companies':
            return <ShopCompanyManagement />;
        case 'shop-orders':
            return <ShopOrderManagement />;
        case 'maintenance':
            return <MaintenanceManagement />;
        case 'operations':
            return <OperationsManagement />;
        case 'smtp':
            return <SettingsManagement />;
        case 'order-lifetime':
            return <OrderLifetimeManagement />;
        case 'integrations':
        default:
            return <IntegrationsSection currentUser={currentUser} />;
    }
};
